import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icon, type IconName } from '../../components/ui/Icon.js';
import { useMemberPreferences } from './api.js';
import { useQuickAccess } from './QuickAccessProvider.js';
import { itemKey, type FavoriteItem } from './store.js';

/** Icon shown on a record chip, by entity type (mirrors QuickAccessMenus). */
const ENTITY_ICON: Record<string, IconName> = {
  customer: 'customers',
  project: 'projects',
  agreement: 'agreements',
  invoice: 'invoices',
  opportunity: 'reports',
  ticket: 'tickets',
  timeEntry: 'time',
  onboarding: 'approvals',
};

function iconFor(item: FavoriteItem): IconName {
  return item.kind === 'menu' ? (item.icon as IconName) : (ENTITY_ICON[item.entity] ?? 'dashboard');
}

/**
 * The pinned favorites as chips across the top bar (the space to the left of the Recent
 * button). Shown only when the member turns it on from the Favorites popover. Chips
 * navigate on click and reorder by drag — the order is the favorites list order, so it
 * persists with the member's quick-access data.
 */
export function FavoritesBar(): JSX.Element | null {
  const navigate = useNavigate();
  const { favorites, moveFavorite } = useQuickAccess();
  const { data: preferences } = useMemberPreferences();
  const [dragKey, setDragKey] = useState<string | null>(null);

  if (!preferences?.showFavoritesBar || favorites.length === 0) return null;

  return (
    <div className="hidden min-w-0 flex-1 items-center justify-end gap-1 overflow-x-auto md:flex">
      {favorites.map((item) => {
        const key = itemKey(item);
        const dragging = dragKey === key;
        return (
          <button
            key={key}
            type="button"
            draggable
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = 'move';
              setDragKey(key);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              if (dragKey && dragKey !== key) moveFavorite(dragKey, key);
            }}
            onDragEnd={() => setDragKey(null)}
            onClick={(event) => {
              if (event.ctrlKey || event.metaKey) {
                window.open(item.path, '_blank', 'noopener,noreferrer');
                return;
              }
              navigate(item.path);
            }}
            className={`inline-flex max-w-48 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 hover:border-brand-300 hover:bg-brand-50 hover:text-slate-900 ${
              dragging ? 'opacity-50' : ''
            }`}
            title={`${item.label} (drag to reorder)`}
          >
            <Icon name={iconFor(item)} className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}
